import useApiCallService from "../../Common/Helpers/ApiCall";
import {CustomButton} from "../../Common/Components/Button/CustomButton";
import { Button, Col } from "reactstrap";
import {useTranslation} from "react-i18next";
import 'react-datepicker/dist/react-datepicker.css';
import SortableList from "../../Common/Components/Lists/SortableList";
import ResponseSalarySheetModel from "../Models/ResponseSalarySheetModel";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import { useEffect, useState } from "react";
import SalarySheetHeader from "./SalarySheetHeader";
import { CheckMobile } from "../../AppSystem/Helpers/CheckMobile";
import { SlidePanel } from "../../Common/Components/Modal/SlidePanel";
import ReportProceedingInfo from "../../Reimbursement/Views/ReportProceedingInfo";

const getTransactionAmount = (item, modelName) => {
    if (!item?.salaryTransactions) return 0;
    const model = item.salaryTransactions.find(st => st.model === modelName);
    if (!model?.transactionTypes) return 0;
    const anvandarlon = model.transactionTypes.find(tt => tt.type === 'Användarlön');
    return anvandarlon?.amount ?? 0;
};

const SalarySheet = () => {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();
    const isMobile = CheckMobile(998);
    const [summaryItems, setSummaryItems] = useState([]);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [reimbursementSummary, setReimbursementSummary] = useState({});
    const [infoPanelOpen, setInfoPanelOpen] = useState(false);
    const [infoPanelItemId, setInfoPanelItemId] = useState(null);
    const [infoPanelVisualId, setInfoPanelVisualId] = useState(null);

    useEffect(() => {
        setPageTitle(t('modelLabel.salarySheet'));
        setPageTitleKey('modelLabel.salarySheet');
        setOnBack(null);
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t]);

    useEffect(() => {
        const url = `/salary-sheet/recent-not-exported`;
        setSummaryLoading(true);
        ApiCall({ method: "get", url }, (response) => {
            setSummaryLoading(false);
            const items = Array.isArray(response) ? response : [];
            // Filter to only include salary sheets from the current month
            const now = new Date();
            const currentYear = now.getFullYear();
            const currentMonth = now.getMonth(); // 0-based
            const filtered = items.filter(item => {
                if (!item?.date) return false;
                const d = new Date(item.date);
                return !isNaN(d.getTime()) && d.getFullYear() === currentYear && d.getMonth() === currentMonth;
            });
            setSummaryItems(filtered);
        });
    }, []);

    // Fetch reimbursement items for each unique month found in summaryItems, filtered by the salary sheet's org
    useEffect(() => {
        if (summaryItems.length === 0) return;

        const monthOrgMap = {};
        summaryItems.forEach(item => {
            if (!item?.date || !item?.orgName) return;
            const parsed = new Date(item.date);
            if (isNaN(parsed.getTime())) return;
            const monthKey = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
            if (!monthOrgMap[monthKey]) {
                monthOrgMap[monthKey] = { orgNames: new Set(), salaryItems: [] };
            }
            monthOrgMap[monthKey].orgNames.add(item.orgName);
            monthOrgMap[monthKey].salaryItems.push(item);
        });

        Object.entries(monthOrgMap).forEach(([monthKey, meta]) => {
            const [year, monthNum] = monthKey.split("-").map(Number);
            const start = `${year}-${String(monthNum).padStart(2, "0")}-01`;
            const lastDay = new Date(year, monthNum, 0).getDate();
            const end = `${year}-${String(monthNum).padStart(2, "0")}-${lastDay}`;
            const url = `/reimbursement/search/filter?datefromdate=${start}&datetodate=${end}&page=1&perPage=100000`;

            ApiCall({ method: "get", url }, (response) => {
                const allItems = response?.items ?? [];
                // Filter to only approved/paid reimbursements that match the salary sheet org names
                const filtered = allItems.filter(i =>
                    (i.status === "Godkänd" || i.status === "Utbetald") &&
                    [...meta.orgNames].some(orgName => i.orgName.includes(orgName) || orgName.includes(i.orgName))
                );
                setReimbursementSummary(prev => ({ ...prev, [monthKey]: filtered }));
            });
        });
    }, [summaryItems]);

    const groups = summaryItems.map((item, index) => {
        const reimbursementAmount = getTransactionAmount(item, 'Förrättning');
        const arvodeAmount = getTransactionAmount(item, 'Arvode');

        let periodFirst = '';
        let periodSecond = '';
        let monthKey = '';
        if (item?.date) {
            const parsed = new Date(item.date);
            if (!isNaN(parsed.getTime())) {
                const year = parsed.getFullYear();
                const monthNum = parsed.getMonth() + 1;
                const lastDay = new Date(year, monthNum, 0).getDate();
                const monthSv = parsed.toLocaleDateString("sv-SE", { month: "long" });
                const monthSvCapitalized = monthSv.charAt(0).toUpperCase() + monthSv.slice(1);
                periodFirst = `Period: 1 – ${lastDay} ${monthSvCapitalized}`;
                periodSecond = `Period: ${monthNum}/12`;
                monthKey = `${year}-${String(monthNum).padStart(2, "0")}`;
            }
        }

        const reimbursementItems = reimbursementSummary[monthKey] ?? [];

        return {
            id: item?.id ?? index,
            label: t("salarySheet.info") + (item?.orgName ? ": " + item?.orgName : ""),
            totalUserSalaryAmount: item?.totalUserSalaryAmount ?? 0,
            cards: [
                {
                    key: `reimbursement-${index}`,
                    title: "Ersättningsbegäran",
                    amount: reimbursementAmount,
                    subtitle: periodFirst,
                    bg: "#E8F5EE", accent: "#157553",
                    icon: <i className="bx bx-receipt rh-theme-icon rh-theme-icon-paid" />,
                    items: reimbursementItems,
                },
                {
                    key: `honorarium-${index}`,
                    title: "Årsarvode",
                    amount: arvodeAmount,
                    subtitle: periodSecond,
                    bg: "#E8F5EE", accent: "#157553",
                    icon: <i className="bx bxs-file rh-theme-icon rh-theme-icon-paid" />,
                },
            ],
        };
    });

    const handleDownloadApiResponse  = (response, name) => {
        
        let file;
        if (response instanceof Blob) {
          file = response;
          
        } else {
          file = new Blob([response]);
        }
        let fileName = name;

        if (!name || name.trim() === "") {
            fileName = "file.pdf";
        } else {
            fileName = name;
        }
        
        const urlToFile = URL.createObjectURL(file);

        const link = document.createElement('a');
        link.href = urlToFile;
        link.setAttribute('download', fileName);

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
    }

    const DownloadFile = async (id, fileName) => {
        let url = `/file?id=${id}`        
        const apiCallOptions = {
            method: "get",
            url: url,
            dataTypes: 'blob',
            responseType: 'blob'
        };
        try {
            await ApiCall(apiCallOptions, (response) => handleDownloadApiResponse(response, fileName));

        } catch (error) {
            console.log(error);
        }
    };
    const renderActionColumn = (item) => {
        return (
            <>
                <CustomButton icon="bx bxs-download pb-1" iconColor="#157553" hoverIconColor='white' hoverButtonColor='#A3C2B8' iconSize={4}  buttonColor="#cde0da"  tooltipText={t('modelLabel.download')} customClasses="rounded me-md-10" buttonBorder={false} customClassesTooltip="tooltip-custom-button-table"
                              id={item.fileId}
                              onClick={() => {
                              DownloadFile(item.fileId, item.fileName);
                            }}
                />
            </>
        );
    }

    const columns = ['date', 'totalAmountUserSalary', "fileCreatedAt"];

    const listOptions = {
        "model": ResponseSalarySheetModel,
        'columns': columns,
        "actions": {
            "actionColumn" : renderActionColumn,
        },
        "exportIsAvailable": true,
        "urlGetListItemsToExport": "/salary-sheet/search/filter",
        "nameFile": t('modelLabel.salarySheet'),
        "ignoreFields" : columns,
        "modelType" : "SalarySheet",
        "filterTestIsAvailable": true,
        "urlGetSearch" : "/salary-sheet/search/filter",
        "viewName": "SalarySheetList"
    };

    const renderReimbursementActions = (item) => {
        return (
            <>
                <Button
                    className="bg-green-light text-green border-0 position-relative reimbursement-report-action-btn"
                    onClick={() => {
                        setInfoPanelItemId(item.id);
                        setInfoPanelVisualId(item.visualId);
                        setInfoPanelOpen(true);
                    }}
                >
                    <i className="bx bx-show fs-5 align-middle pb-1" />
                    <div className="tooltip-custom-button">{t("button.info")}</div>
                </Button>
            </>
        );
    };

    return (
        <div className='mb-5 px-0 mx-0'>
            <SlidePanel
                isOpen={infoPanelOpen}
                onClose={() => setInfoPanelOpen(false)}
                title={t('reimbursement.reportProcessing') + (infoPanelVisualId ? ' ' + infoPanelVisualId : '')}
            >
                {infoPanelItemId && <ReportProceedingInfo id={infoPanelItemId} onClose={() => setInfoPanelOpen(false)} />}
            </SlidePanel>

            <Col lg="12" className={isMobile ? "p-0" : "p-4 mx-0 px-0 pt-0"}>
                <SalarySheetHeader
                    title={t("modelLabel.salarySheet")}
                    groups={groups}
                    loading={summaryLoading}
                    renderItemActions={renderReimbursementActions}
                />
                {isMobile && <hr/>}
                <SortableList
                    listOptions={listOptions}
                />
            </Col>
        </div>
    );
};

export default SalarySheet;